"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "../lib/cn";
import { Label } from "../primitives/Label";

/**
 * TagInput — controlled multi-tag field: type and press Enter to add a chip,
 * Backspace on empty input to pop the last one, with an optional filtered
 * suggestion list underneath.
 *
 * Ported from Creator Studio v2 (`src/components/audience/TagInputField.tsx`)
 * and re-themed: every inline navy hex (`#0A2540` borders, chip fills, hover
 * washes) is replaced with `--input` / `--ring` / `--primary` / `--muted`
 * tokens, the label renders through the `<Label>` primitive, and the
 * suggestion list gains keyboard navigation (ArrowUp / ArrowDown / Enter /
 * Escape) plus listbox ARIA the original lacked.
 *
 * Controlled only: `value` is the current tag list, `onChange` receives the
 * next list on every add / remove. Duplicates are ignored (exact match).
 *
 * `className` styles the root; the forwarded ref reaches the inner
 * `<input>` so consumers can focus it imperatively.
 *
 * @example
 *   const [tags, setTags] = React.useState<string[]>(["creators"]);
 *   <TagInput
 *     label="Audience tags"
 *     value={tags}
 *     onChange={setTags}
 *     suggestions={["creators", "educators", "founders"]}
 *   />
 */
export interface TagInputProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue"> {
  /** Field label rendered above the control via the `<Label>` primitive. */
  label?: React.ReactNode;
  /** Current tags (controlled). */
  value: readonly string[];
  /** Receives the next tag list on add / remove. */
  onChange: (tags: string[]) => void;
  /** Pool of suggestions, filtered by the current input text. */
  suggestions?: readonly string[];
  /** Max suggestions shown at once. Default 6. */
  maxSuggestions?: number;
  /** Input placeholder, shown while no tags exist. */
  placeholder?: string;
  /** Disable the whole control. */
  disabled?: boolean;
}

export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      label,
      value,
      onChange,
      suggestions = [],
      maxSuggestions = 6,
      placeholder = "Type and press Enter",
      disabled = false,
      className,
      id,
      ...rest
    },
    ref,
  ) => {
    const autoId = React.useId();
    const inputId = id ?? `tag-input-${autoId}`;
    const listboxId = `${inputId}-listbox`;

    const [input, setInput] = React.useState("");
    const [focused, setFocused] = React.useState(false);
    const [highlighted, setHighlighted] = React.useState(-1);

    const filtered = React.useMemo(() => {
      if (!input) return [];
      const query = input.toLowerCase();
      return suggestions
        .filter((s) => s.toLowerCase().includes(query) && !value.includes(s))
        .slice(0, maxSuggestions);
    }, [input, suggestions, value, maxSuggestions]);

    const open = focused && filtered.length > 0;

    const addTag = (tag: string) => {
      const trimmed = tag.trim();
      if (trimmed && !value.includes(trimmed)) {
        onChange([...value, trimmed]);
      }
      setInput("");
      setHighlighted(-1);
    };

    const removeTag = (idx: number) => {
      onChange(value.filter((_, i) => i !== idx));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (open && highlighted >= 0 && filtered[highlighted]) {
          addTag(filtered[highlighted]);
        } else if (input.trim()) {
          addTag(input);
        }
      } else if (e.key === "ArrowDown" && open) {
        e.preventDefault();
        setHighlighted((h) => (h + 1) % filtered.length);
      } else if (e.key === "ArrowUp" && open) {
        e.preventDefault();
        setHighlighted((h) => (h <= 0 ? filtered.length - 1 : h - 1));
      } else if (e.key === "Escape" && open) {
        setHighlighted(-1);
        setInput("");
      } else if (e.key === "Backspace" && !input && value.length) {
        removeTag(value.length - 1);
      }
    };

    return (
      <div className={cn("relative", disabled && "pointer-events-none opacity-50", className)} {...rest}>
        {label ? (
          <Label htmlFor={inputId} className="mb-1.5 block">
            {label}
          </Label>
        ) : null}

        <div
          data-slot="tag-input-field"
          className={cn(
            "flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-3 py-2",
            "transition-colors duration-180 ease-smooth-out",
            "focus-within:border-ring focus-within:ring-1 focus-within:ring-ring",
          )}
        >
          {value.map((tag, i) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-primary/5 py-0.5 pl-2.5 pr-1 text-xs font-medium text-foreground"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(i)}
                disabled={disabled}
                aria-label={`Remove ${tag}`}
                className="rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <X className="size-3" aria-hidden />
              </button>
            </span>
          ))}
          <input
            ref={ref}
            id={inputId}
            value={input}
            disabled={disabled}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            onChange={(e) => {
              setInput(e.target.value);
              setHighlighted(-1);
            }}
            onFocus={() => setFocused(true)}
            // Delay so suggestion onMouseDown wins over the blur close.
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ""}
            className="min-w-[120px] flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>

        {open ? (
          <div
            id={listboxId}
            role="listbox"
            className="absolute z-50 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-border bg-popover py-1 text-popover-foreground shadow-md"
          >
            {filtered.map((s, i) => (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={i === highlighted}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(s);
                }}
                onMouseEnter={() => setHighlighted(i)}
                className={cn(
                  "block w-full px-3 py-2 text-left text-sm transition-colors duration-180",
                  i === highlighted ? "bg-muted" : "hover:bg-muted/60",
                )}
              >
                {s}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);
TagInput.displayName = "TagInput";
